import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  Divider,
  Space,
  Table,
  Typography,
  Upload,
  InputNumber,
  Modal,
} from "antd";
import {
  PlusOutlined,
  SaveOutlined,
  InboxOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { formatCurrencyWithConversion } from "../../../utils/currencyUtils";

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

const StableDatePicker = React.memo(
  function StableDatePicker({ value, onChange, style, getPopupContainer }) {
    return (
      <DatePicker
        value={value}
        onChange={onChange}
        style={style}
        getPopupContainer={getPopupContainer || ((trigger) => trigger.parentElement)}
      />
    );
  },
  (prev, next) => prev.value?.valueOf() === next.value?.valueOf()
);
const { Dragger } = Upload;

const CustomCurrencyModal = ({
  visible,
  onCancel,
  onSave,
  currencies,
  rates,
}) => {
  const [form] = Form.useForm();
  const [currencyList, setCurrencyList] = useState([]);

  useEffect(() => {
    if (visible) {
      // Initialize with default + custom currencies
      const defaultCurrencies = [
        { code: "USD", symbol: "$", rate: 1.0, isDefault: true },
        { code: "EUR", symbol: "€", isDefault: true },
        { code: "GBP", symbol: "£", isDefault: true },
      ];
      const customCurrencies = currencies
        .filter((c) => !defaultCurrencies.some((dc) => dc.code === c.code))
        .map((c) => ({ ...c, rate: rates[c.code] || "" }));

      const allCurrencies = defaultCurrencies.map((dc) => ({
        ...dc,
        rate: rates[dc.code] || dc.rate || "",
      }));

      // Add custom currencies not in default list
      customCurrencies.forEach((cc) => {
        if (!allCurrencies.some((ac) => ac.code === cc.code)) {
          allCurrencies.push(cc);
        }
      });

      setCurrencyList(allCurrencies.filter((c) => c.code !== "USD")); // Don't allow editing USD
    }
  }, [visible, currencies, rates]);

  const handleSave = () => {
    const newRates = {};
    const newCurrencies = [
      { code: "USD", symbol: "$" },
      ...currencyList.map(({ code, symbol }) => ({ code, symbol })),
    ];
    currencyList.forEach((c) => {
      if (c.rate) {
        newRates[c.code] = parseFloat(c.rate);
      }
    });
    onSave(newCurrencies, newRates);
    onCancel();
  };

  const handleCurrencyChange = (index, field, value) => {
    const newList = [...currencyList];
    newList[index][field] = value;
    setCurrencyList(newList);
  };

  const addCurrency = () => {
    setCurrencyList([
      ...currencyList,
      { code: "", symbol: "", rate: "", isDefault: false },
    ]);
  };

  const removeCurrency = (index) => {
    const newList = [...currencyList];
    newList.splice(index, 1);
    setCurrencyList(newList);
  };

  return (
    <Modal
      title="Add / Edit Custom Currencies"
      visible={visible}
      onOk={handleSave}
      onCancel={onCancel}
      width={600}
    >
      <Form layout="vertical">
        {currencyList.map((currency, index) => (
          <Row key={index} gutter={16} align="middle">
            <Col span={6}>
              <Input
                placeholder="Code (e.g., INR)"
                value={currency.code}
                onChange={(e) =>
                  handleCurrencyChange(
                    index,
                    "code",
                    e.target.value.toUpperCase()
                  )
                }
                disabled={currency.isDefault}
              />
            </Col>
            <Col span={4}>
              <Input
                placeholder="Symbol (e.g., ₹)"
                value={currency.symbol}
                onChange={(e) =>
                  handleCurrencyChange(index, "symbol", e.target.value)
                }
                disabled={currency.isDefault}
              />
            </Col>
            <Col span={10}>
              <InputNumber
                placeholder="Rate to 1 USD"
                value={currency.rate}
                onChange={(value) => handleCurrencyChange(index, "rate", value)}
                min={0}
                style={{ width: "100%" }}
                addonBefore="1 USD ="
              />
            </Col>
            <Col span={4}>
              {!currency.isDefault && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeCurrency(index)}
                />
              )}
            </Col>
          </Row>
        ))}
        <Button
          type="dashed"
          onClick={addCurrency}
          icon={<PlusOutlined />}
          style={{ marginTop: "16px" }}
        >
          Add Currency
        </Button>
      </Form>
    </Modal>
  );
};

const InvoiceFormLayout = ({
  form,
  isEditMode,
  loading,
  invoiceItems,
  setInvoiceItems,
  partners,
  paymentTerms = [],
  // categories is passed but not directly used in this component
  columns,
  calculatedValues,
  onFinish,
  handlePartnerChange,
  addItem,
  addPaymentTerm,
  removePaymentTerm,
  updatePaymentTerm,
  navigate,
  selectedBrand,
  onValuesChange,
  brands = [],
  onBrandChange,
  defaultInvoiceType = "invoice",
  hidePaymentTerms = false,
  typeFieldName = "invoiceType",
  typeOptions = [
    { value: "invoice", label: "Invoice" },
    { value: "bill", label: "Bill" },
  ],
  typeLabel = "Type",
  numberLabel = "Invoice Number",
  numberPlaceholder = "e.g. INV-2025-001",
  // Optional overrides for reusing this layout (e.g., Cash/Credit)
  formTitle,
  itemsLabel,
  fileLabel,
  submitLabel,
  cancelPath = "/accounting/invoices",
  onSplitInvoice,
  splitButtonText,
  // Bank field for cash payments
  banks = [],
  showBankField = false,
}) => {
  const [customCurrencyModalVisible, setCustomCurrencyModalVisible] =
    useState(false);
  const [currencies, setCurrencies] = useState([
    { code: "USD", symbol: "$" },
    { code: "EUR", symbol: "€" },
    { code: "GBP", symbol: "£" },
  ]);
  const [customRates, setCustomRates] = useState({});

  useEffect(() => {
    try {
      const savedCurrencies = localStorage.getItem("custom_currencies");
      const savedRates = localStorage.getItem("custom_currency_rates");
      if (savedCurrencies) {
        setCurrencies(JSON.parse(savedCurrencies));
      }
      if (savedRates) {
        setCustomRates(JSON.parse(savedRates));
      }
    } catch (e) {
      console.error("Failed to load custom currencies from localStorage", e);
    }
  }, []);

  const handleSaveCustomCurrencies = (newCurrencies, newRates) => {
    setCurrencies(newCurrencies);
    setCustomRates(newRates);
    localStorage.setItem("custom_currencies", JSON.stringify(newCurrencies));
    localStorage.setItem("custom_currency_rates", JSON.stringify(newRates));
  };

  const handleCurrencyChange = (value) => {
    if (value === "add_new") {
      setCustomCurrencyModalVisible(true);
      // Reset the form field to its previous value to avoid "add_new" being selected
      setTimeout(() => {
        form.setFieldsValue({ currency: form.getFieldValue("currency") });
      }, 0);
    }
  };
  // Get the current currency value from the form with a space after it for better formatting
  const getCurrencySymbol = () => (form.getFieldValue("currency") || "$") + " ";
  const getCurrentCurrency = () => form.getFieldValue("currency") || "$";
  const INVOICE_DRAFT_KEY = "invoiceDraft";

  if (!selectedBrand && brands.length === 0) {
    return (
      <Card
        title={`Create ${defaultInvoiceType === "bill" ? "Bill" : "Invoice"}`}
        bordered={false}
      >
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Title level={4}>Loading brands...</Title>
        </div>
      </Card>
    );
  }

  const clearDraft = () => {
    sessionStorage.removeItem(INVOICE_DRAFT_KEY);
    console.log("🗑️ Invoice draft cleared");
  };

  // Payment terms columns for the table
  const paymentTermColumns = [
    {
      title: "Percentage (%)",
      dataIndex: "percentage",
      key: "percentage",
      render: (text, record) => (
        <InputNumber
          value={text}
          min={0}
          max={100}
          step={1}
          precision={2}
          onChange={(value) =>
            updatePaymentTerm(record.id, "percentage", value)
          }
          formatter={(value) => {
            if (!value) return "";
            return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          }}
          parser={(value) => {
            if (!value) return "";
            return value.replace(/,/g, "");
          }}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Due Days",
      dataIndex: "due_days",
      key: "due_days",
      render: (text, record) => (
        <InputNumber
          value={text}
          min={0}
          step={1}
          precision={0}
          onChange={(value) => updatePaymentTerm(record.id, "due_days", value !== null && value !== undefined ? value : 0)}
          formatter={(value) => {
            // Handle 0 explicitly - it's a valid value meaning "due today"
            if (value === null || value === undefined || value === "") return "";
            return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          }}
          parser={(value) => {
            // Handle empty string by returning 0 (due today)
            if (value === null || value === undefined || value === "") return 0;
            const parsed = value.replace(/,/g, "");
            return parsed === "" ? 0 : Number(parsed);
          }}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Due Date",
      key: "due_date",
      render: (_, record) => {
        const issueDate = form.getFieldValue("issueDate");
        // Use issue date if available, otherwise use today's date as fallback
        const baseDate = issueDate ? dayjs(issueDate) : dayjs();
        // Calculate due date: base date + due_days (0 means due today)
        const dueDays =
          record.due_days !== undefined && record.due_days !== null
            ? parseFloat(record.due_days)
            : 0;
        // Ensure dueDays is a valid number
        const validDueDays = isNaN(dueDays) ? 0 : Math.max(0, dueDays);
        const dueDate = baseDate.add(validDueDays, "days");

        return (
          <StableDatePicker
            value={dueDate}
            style={{ width: "100%" }}
            onChange={(date) => {
              if (date) {
                const diff = date.diff(baseDate, "days");
                updatePaymentTerm(record.id, "due_days", Math.max(0, diff));
              }
            }}
          />
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text) => (
        <span>
          {getCurrencySymbol()}
          {parseFloat(text || 0).toLocaleString("en-US", {
            minimumFractionDigits: getDecimalPrecision(),
            maximumFractionDigits: getDecimalPrecision(),
          })}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removePaymentTerm(record.id)}
        />
      ),
    },
  ];

  // Determine the document type label based on defaultInvoiceType prop
  const documentTypeLabel = defaultInvoiceType === "bill" ? "Bill" : "Invoice";

  // Resolve labels with defaults based on document type
  const resolvedItemsLabel = itemsLabel || `${documentTypeLabel} Items`;
  const resolvedFileLabel = fileLabel || `Upload ${documentTypeLabel} File`;

  const resolvedTitle =
    formTitle ||
    (isEditMode ? `Edit ${documentTypeLabel}` : `Create ${documentTypeLabel}`);

  const resolvedSubmitLabel =
    submitLabel ||
    (isEditMode
      ? `Update ${documentTypeLabel}`
      : `Create ${documentTypeLabel}`);

  return (
    <>
      <CustomCurrencyModal
        visible={customCurrencyModalVisible}
        onCancel={() => setCustomCurrencyModalVisible(false)}
        onSave={handleSaveCustomCurrencies}
        currencies={currencies}
        rates={customRates}
      />
      <Card title={resolvedTitle}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={onValuesChange}
          initialValues={{
            invoiceType: "invoice",
            issueDate: dayjs(),
            currency: "$",
          }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="brand"
                label="Brand"
                rules={[{ required: true, message: "Please select a brand" }]}
              >
                <Select
                  placeholder="Select a brand"
                  onChange={onBrandChange}
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {brands.map((brand) => (
                    <Option key={brand.id} value={brand.id}>
                      {brand.brand_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name={typeFieldName}
                label={typeLabel}
                rules={[
                  {
                    required: true,
                    message: `Please select ${typeLabel.toLowerCase()}`,
                  },
                ]}
              >
                <Select>
                  {typeOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="number"
                label={numberLabel}
                rules={[
                  {
                    required: true,
                    message: `Please enter ${numberLabel.toLowerCase()}`,
                  },
                ]}
              >
                <Input placeholder={numberPlaceholder} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="currency"
                label="Currency"
                rules={[
                  { required: true, message: "Please select a currency" },
                ]}
              >
                <Select
                  placeholder="Select currency"
                  style={{ width: "100%" }}
                  onChange={handleCurrencyChange}
                >
                  {currencies.map((c) => (
                    <Option key={c.code} value={c.symbol}>
                      {c.symbol} - {c.code}
                    </Option>
                  ))}
                  <Select.Option key="add_new" value="add_new">
                    <span style={{ color: "#1890ff" }}>
                      <PlusOutlined /> Add / Edit Currencies
                    </span>
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="partner"
                label={
                  <Space>
                    <span>Partner</span>
                    <Button
                      type="primary"
                      style={{
                        fontSize: "10px",
                        padding: "0 6px",
                        height: "20px",
                      }}
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        navigate("/accounting/settings", {
                          state: { activeTab: "3" },
                        })
                      }
                    >
                      Add
                    </Button>
                  </Space>
                }
                rules={[{ required: true, message: "Please select a partner" }]}
              >
                <Select
                  placeholder="Select a partner"
                  onChange={handlePartnerChange}
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {partners.map((partner) => (
                    <Option key={partner.id} value={partner.id}>
                      {partner.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {showBankField && (
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item
                  name="bank"
                  label="Bank"
                  rules={[{ required: false, message: "Please select a bank" }]}
                >
                  <Select
                    placeholder="Select a bank (optional)"
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {banks.map((bank) => (
                      <Option key={bank.id} value={bank.id}>
                        {bank.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="issueDate"
                label="Issue Date"
                rules={[
                  { required: true, message: "Please select issue date" },
                ]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">{resolvedItemsLabel}</Divider>

          <Button
            type="dashed"
            onClick={addItem}
            style={{ marginBottom: 16 }}
            icon={<PlusOutlined />}
          >
            Add Item
          </Button>

          <Table
            columns={columns}
            dataSource={invoiceItems}
            pagination={false}
            rowKey="key"
            size="small"
            expandable={{
              expandedRowRender: (record, index) => {
                // Initialize payment_terms array if it doesn't exist
                const itemPaymentTerms = record.payment_terms || [];

                // Get line number (1-based index)
                const lineNumber =
                  invoiceItems.findIndex((item) => item.key === record.key) + 1;

                // Get issue date from form
                const issueDateValue = form.getFieldValue("issueDate");
                const baseDate = issueDateValue
                  ? dayjs(issueDateValue)
                  : dayjs();

                // Calculate item subtotal for amount calculation
                const itemSubtotal = parseFloat(record.subtotal || 0);
                const currencySymbol = getCurrencySymbol();

                const addItemPaymentTerm = () => {
                  // Calculate remaining percentage to distribute
                  const totalPercentage = itemPaymentTerms.reduce(
                    (sum, term) => sum + parseFloat(term.percentage || 0),
                    0
                  );
                  const remainingPercentage = Math.max(
                    0,
                    100 - totalPercentage
                  );

                  // Calculate due_days incrementing by 30 (30, 60, 90, 120, etc.)
                  const maxDueDays =
                    itemPaymentTerms.length > 0
                      ? Math.max(
                          ...itemPaymentTerms.map((term) =>
                            parseFloat(term.due_days || 0)
                          )
                        )
                      : 0;
                  const nextDueDays = maxDueDays + 30;

                  // If there are existing terms and remaining percentage is 0, split equally
                  if (
                    itemPaymentTerms.length > 0 &&
                    remainingPercentage === 0
                  ) {
                    // Distribute equally among all terms (including new one)
                    const totalTerms = itemPaymentTerms.length + 1;
                    const basePercentage = Math.floor(100 / totalTerms);
                    const remainder = 100 - basePercentage * totalTerms;

                    // Distribute base percentage to all existing terms
                    // Remainder will go to the last term (new term)
                    const updatedTerms = itemPaymentTerms.map((term) => ({
                      ...term,
                      percentage: basePercentage,
                    }));

                    // New term gets base percentage + all remainder
                    const newTerm = {
                      id: Date.now() + Math.random(),
                      percentage: basePercentage + remainder,
                      due_days: nextDueDays,
                    };

                    const updatedItems = invoiceItems.map((item) =>
                      item.key === record.key
                        ? { ...item, payment_terms: [...updatedTerms, newTerm] }
                        : item
                    );
                    if (setInvoiceItems) {
                      setInvoiceItems(updatedItems);
                    }
                  } else {
                    // Use remaining percentage or default to 100 if no terms exist
                    const newTerm = {
                      id: Date.now() + Math.random(),
                      percentage:
                        remainingPercentage > 0
                          ? remainingPercentage
                          : itemPaymentTerms.length === 0
                          ? 100
                          : 0,
                      due_days:
                        itemPaymentTerms.length === 0 ? 30 : nextDueDays,
                    };
                    const updatedItems = invoiceItems.map((item) =>
                      item.key === record.key
                        ? {
                            ...item,
                            payment_terms: [...itemPaymentTerms, newTerm],
                          }
                        : item
                    );
                    if (setInvoiceItems) {
                      setInvoiceItems(updatedItems);
                    }
                  }
                };

                const removeItemPaymentTerm = (termId) => {
                  const remainingTerms = itemPaymentTerms.filter(
                    (t) => t.id !== termId
                  );

                  // If terms remain, redistribute percentages evenly
                  let finalTerms = remainingTerms;
                  if (remainingTerms.length > 0) {
                    const basePercentage = Math.floor(
                      100 / remainingTerms.length
                    );
                    const remainder =
                      100 - basePercentage * remainingTerms.length;

                    finalTerms = remainingTerms.map((term, index) => ({
                      ...term,
                      // Give remainder to the last term
                      percentage:
                        basePercentage +
                        (index === remainingTerms.length - 1 ? remainder : 0),
                    }));
                  }

                  const updatedItems = invoiceItems.map((item) =>
                    item.key === record.key
                      ? { ...item, payment_terms: finalTerms }
                      : item
                  );
                  if (setInvoiceItems) {
                    setInvoiceItems(updatedItems);
                  }
                };

                const updateItemPaymentTerm = (termId, field, value) => {
                  let updatedTerms;
                  if (field === "percentage") {
                    // When updating percentage, ensure total doesn't exceed 100
                    const currentTotal = itemPaymentTerms.reduce(
                      (sum, term) => {
                        if (term.id === termId) {
                          return sum + parseFloat(value || 0);
                        }
                        return sum + parseFloat(term.percentage || 0);
                      },
                      0
                    );

                    // If total exceeds 100, cap the value
                    const otherTermsTotal = itemPaymentTerms
                      .filter((t) => t.id !== termId)
                      .reduce(
                        (sum, term) => sum + parseFloat(term.percentage || 0),
                        0
                      );
                    const maxAllowed = 100 - otherTermsTotal;
                    const cappedValue = Math.min(
                      parseFloat(value || 0),
                      Math.max(0, maxAllowed)
                    );

                    updatedTerms = itemPaymentTerms.map((t) =>
                      t.id === termId ? { ...t, [field]: cappedValue } : t
                    );
                  } else {
                    updatedTerms = itemPaymentTerms.map((t) =>
                      t.id === termId ? { ...t, [field]: value } : t
                    );
                  }

                  const updatedItems = invoiceItems.map((item) =>
                    item.key === record.key
                      ? { ...item, payment_terms: updatedTerms }
                      : item
                  );
                  if (setInvoiceItems) {
                    setInvoiceItems(updatedItems);
                  }
                };

                const itemPaymentTermColumns = [
                  {
                    title: "Percentage (%)",
                    dataIndex: "percentage",
                    key: "percentage",
                    render: (text, term) => {
                      const totalPercentage = itemPaymentTerms.reduce(
                        (sum, t) => sum + parseFloat(t.percentage || 0),
                        0
                      );
                      const otherTermsTotal = itemPaymentTerms
                        .filter((t) => t.id !== term.id)
                        .reduce(
                          (sum, t) => sum + parseFloat(t.percentage || 0),
                          0
                        );
                      const maxAllowed = 100 - otherTermsTotal;
                      const currentValue = parseFloat(text || 0);
                      const isOverLimit = currentValue > maxAllowed;

                      return (
                        <div>
                          <InputNumber
                            value={text}
                            min={0}
                            max={maxAllowed}
                            step={1}
                            precision={getDecimalPrecision()}
                            onChange={(value) =>
                              updateItemPaymentTerm(
                                term.id,
                                "percentage",
                                value
                              )
                            }
                            formatter={(value) => {
                              if (!value) return "";
                              return `${value}`.replace(
                                /\B(?=(\d{3})+(?!\d))/g,
                                ","
                              );
                            }}
                            parser={(value) => {
                              if (!value) return "";
                              return value.replace(/,/g, "");
                            }}
                            style={{ width: "100%" }}
                            status={isOverLimit ? "error" : ""}
                          />
                          {isOverLimit && (
                            <div
                              style={{
                                fontSize: "10px",
                                color: "#ff4d4f",
                                marginTop: "2px",
                              }}
                            >
                              Max: {maxAllowed.toFixed(2)}%
                            </div>
                          )}
                          {totalPercentage > 100 && (
                            <div
                              style={{
                                fontSize: "10px",
                                color: "#ff4d4f",
                                marginTop: "2px",
                              }}
                            >
                              Total exceeds 100%
                            </div>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    title: "Due Days",
                    dataIndex: "due_days",
                    key: "due_days",
                    render: (text, term) => (
                      <InputNumber
                        value={text}
                        min={0}
                        step={1}
                        precision={0}
                        onChange={(value) =>
                          updateItemPaymentTerm(term.id, "due_days", value !== null && value !== undefined ? value : 0)
                        }
                        formatter={(value) => {
                          // Handle 0 explicitly - it's a valid value meaning "due today"
                          if (value === null || value === undefined || value === "") return "";
                          return `${value}`.replace(
                            /\B(?=(\d{3})+(?!\d))/g,
                            ","
                          );
                        }}
                        parser={(value) => {
                          // Handle empty string by returning 0 (due today)
                          if (value === null || value === undefined || value === "") return 0;
                          const parsed = value.replace(/,/g, "");
                          return parsed === "" ? 0 : Number(parsed);
                        }}
                        style={{ width: "100%" }}
                      />
                    ),
                  },
                  {
                    title: "Due Date",
                    key: "due_date",
                    render: (_, term) => {
                      const issueDate = form.getFieldValue("issueDate");
                      const itemBaseDate = issueDate ? dayjs(issueDate) : dayjs();
                      const dueDays =
                        term.due_days !== undefined && term.due_days !== null
                          ? parseFloat(term.due_days)
                          : 0;
                      const validDueDays = isNaN(dueDays)
                        ? 0
                        : Math.max(0, dueDays);
                      const itemDueDate = itemBaseDate.add(validDueDays, "days");
                      return (
                        <StableDatePicker
                          value={itemDueDate}
                          style={{ width: "100%" }}
                          onChange={(date) => {
                            if (date) {
                              const diff = date.diff(itemBaseDate, "days");
                              updateItemPaymentTerm(term.id, "due_days", Math.max(0, diff));
                            }
                          }}
                        />
                      );
                    },
                  },
                  {
                    title: "Amount",
                    dataIndex: "amount",
                    key: "amount",
                    render: (_, term) => {
                      const percentage = parseFloat(term.percentage || 0);
                      const amount = (itemSubtotal * percentage) / 100;
                      return (
                        <span>
                          {currencySymbol}
                          {amount.toFixed(getDecimalPrecision())}
                        </span>
                      );
                    },
                  },
                  {
                    title: "Action",
                    key: "action",
                    render: (_, term) => (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeItemPaymentTerm(term.id)}
                      />
                    ),
                  },
                ];

                return (
                  <div style={{ padding: "16px", background: "#fafafa" }}>
                    <Divider orientation="left">
                      Payment Terms for Line {lineNumber}
                    </Divider>
                    <Button
                      type="dashed"
                      onClick={addItemPaymentTerm}
                      style={{ marginBottom: 16 }}
                      icon={<PlusOutlined />}
                    >
                      Add Payment Term for Line {lineNumber}
                    </Button>
                    {itemPaymentTerms.length > 0 ? (
                      <Table
                        columns={itemPaymentTermColumns}
                        dataSource={itemPaymentTerms}
                        pagination={false}
                        rowKey="id"
                        size="small"
                      />
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "#999",
                        }}
                      >
                        No payment terms added yet. Click &quot;Add Payment Term
                        for Line {lineNumber}&quot; to create payment terms.
                      </div>
                    )}
                  </div>
                );
              },
              rowExpandable: () => true,
            }}
          />

          {!hidePaymentTerms && (
            <>
              <Divider orientation="left">
                {documentTypeLabel} Payment Terms (Apply to items without
                payment terms)
              </Divider>

              <Button
                type="dashed"
                onClick={addPaymentTerm}
                style={{ marginBottom: 16 }}
                icon={<PlusOutlined />}
              >
                Add Payment Term
              </Button>

              {paymentTerms.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Button
                    type="link"
                    onClick={() => {
                      // Apply invoice-level payment terms to all items that don't have payment terms
                      if (setInvoiceItems && paymentTerms.length > 0) {
                        const updatedItems = invoiceItems.map((item) => {
                          // Only apply if item doesn't have payment terms
                          if (
                            !item.payment_terms ||
                            item.payment_terms.length === 0
                          ) {
                            // Copy payment terms and create new IDs
                            const itemPaymentTerms = paymentTerms.map(
                              (term) => ({
                                ...term,
                                id: Date.now() + Math.random() + Math.random(), // New unique ID
                              })
                            );
                            return { ...item, payment_terms: itemPaymentTerms };
                          }
                          return item;
                        });
                        setInvoiceItems(updatedItems);
                      }
                    }}
                  >
                    Apply to Items Without Payment Terms
                  </Button>
                </div>
              )}

              <Table
                columns={paymentTermColumns}
                dataSource={paymentTerms}
                pagination={false}
                rowKey="id"
                size="small"
              />
            </>
          )}

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} md={8}>
              <div
                style={{
                  padding: "8px 16px",
                  background: "#f5f5f5",
                  borderRadius: 4,
                }}
              >
                <Row justify="space-between">
                  <Col>Subtotal:</Col>
                  <Col>
                    {formatCurrencyWithConversion(
                      calculatedValues.calculated_subtotal || 0,
                      getCurrentCurrency()
                    )}
                  </Col>
                </Row>
                <Row justify="space-between">
                  <Col>Tax:</Col>
                  <Col>
                    {formatCurrencyWithConversion(
                      calculatedValues.calculated_tax_amount || 0,
                      getCurrentCurrency()
                    )}
                  </Col>
                </Row>
                <Row justify="space-between">
                  <Col>Discount:</Col>
                  <Col>
                    {formatCurrencyWithConversion(
                      calculatedValues.calculated_discount_amount || 0,
                      getCurrentCurrency()
                    )}
                  </Col>
                </Row>
                <Divider style={{ margin: "8px 0" }} />
                <Row justify="space-between">
                  <Col>
                    <strong>Total:</strong>
                  </Col>
                  <Col>
                    <strong>
                      {formatCurrencyWithConversion(
                        calculatedValues.calculated_total || 0,
                        getCurrentCurrency()
                      )}
                    </strong>
                  </Col>
                </Row>
                <Row justify="space-between">
                  <Col>
                    <strong
                      style={{
                        color:
                          calculatedValues.calculated_total !==
                          parseFloat(form.getFieldValue("total_amount"))
                            ? "red"
                            : "",
                      }}
                    >
                      {calculatedValues.calculated_total !==
                      parseFloat(form.getFieldValue("total_amount"))
                        ? "Total is not matching to total amount"
                        : " "}
                    </strong>
                  </Col>
                </Row>
              </div>
            </Col>

            {/* Editable form fields for manual adjustment */}
            <Col xs={24} sm={12} md={16}>
              <Row gutter={16}>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    name="subtotal"
                    label="Subtotal"
                    rules={[{ required: false }]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="0.00"
                      precision={getDecimalPrecision()}
                      min={0}
                      step={0.01}
                      stringMode
                      formatter={(value) => {
                        if (!value) return "";
                        const currency = form.getFieldValue("currency") || "$";
                        return `${currency} ${value}`.replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          ","
                        );
                      }}
                      parser={(value) => {
                        if (!value) return "";
                        return value.replace(/[^\d.-]/g, "");
                      }}
                      onWheel={(e) => e.target.blur()}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    name="tax_rate"
                    label="Tax Rate (%)"
                    rules={[{ required: false }]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="0.00"
                      precision={getDecimalPrecision()}
                      min={0}
                      max={100}
                      step={0.01}
                      formatter={(value) => {
                        if (!value) return "";
                        return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                      }}
                      parser={(value) => {
                        if (!value) return "";
                        return value.replace(/,/g, "");
                      }}
                      addonAfter="%"
                      onWheel={(e) => e.target.blur()}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    name="discount_amount"
                    label="Discount Amount"
                    rules={[{ required: false }]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="0.00"
                      precision={getDecimalPrecision()}
                      min={0}
                      step={0.01}
                      stringMode
                      formatter={(value) => {
                        if (!value) return "";
                        const currency = form.getFieldValue("currency") || "$";
                        return `${currency} ${value}`.replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          ","
                        );
                      }}
                      parser={(value) => {
                        if (!value) return "";
                        return value.replace(/[^\d.-]/g, "");
                      }}
                      onWheel={(e) => e.target.blur()}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Form.Item
                    name="total_amount"
                    label="Total Amount"
                    rules={[{ required: false }]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="0.00"
                      precision={getDecimalPrecision()}
                      min={0}
                      step={0.01}
                      stringMode
                      formatter={(value) => {
                        if (!value) return "";
                        const currency = form.getFieldValue("currency") || "$";
                        return `${currency} ${value}`.replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          ","
                        );
                      }}
                      parser={(value) => {
                        if (!value) return "";
                        return value.replace(/[^\d.-]/g, "");
                      }}
                      onWheel={(e) => e.target.blur()}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>

          <Divider />

          <Form.Item name="notes" label="Notes">
            <TextArea rows={4} placeholder="Enter any additional notes here" />
          </Form.Item>

          {/* File URL display - separate from the upload component */}
          {form.getFieldValue("fileUrl") && (
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  padding: "12px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "2px",
                  marginBottom: "8px",
                  background: "#f5f5f5",
                }}
              >
                <p style={{ margin: 0 }}>
                  <strong>Current File:</strong>
                </p>
                <a
                  href={form.getFieldValue("fileUrl")}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#1890ff",
                  }}
                >
                  <svg
                    viewBox="64 64 896 896"
                    focusable="false"
                    data-icon="file-pdf"
                    width="1em"
                    height="1em"
                    fill="currentColor"
                    aria-hidden="true"
                    style={{ marginRight: "8px" }}
                  >
                    <path d="M531.3 574.4l.3-1.4c5.8-23.9 13.1-53.7 7.4-80.7-3.8-21.3-19.5-29.6-32.9-30.2-15.8-.7-29.9 8.3-33.4 21.4-6.6 24-.7 56.8 10.1 98.6-13.6 32.4-35.3 79.5-51.2 107.5-29.6 15.3-69.3 38.9-75.2 68.7-1.2 5.5.2 12.5 3.5 18.8 3.7 7 9.6 12.4 16.5 15 3 1.1 6.6 2 10.8 2 17.6 0 46.1-14.2 84.1-79.4 5.8-1.9 11.8-3.9 17.6-5.9 27.2-9.2 55.4-18.8 80.9-23.1 28.2 15.1 60.3 24.8 82.1 24.8 21.6 0 30.1-12.8 33.3-20.5 5.6-13.5 2.9-30.5-6.2-39.6-13.2-13-45.3-16.4-95.3-10.2-24.6-15-40.7-35.4-52.4-65.8zM421.6 726.3c-13.9 20.2-24.4 30.3-30.1 34.7 6.7-12.3 19.8-25.3 30.1-34.7zm87.6-235.5c5.2 8.9 4.5 35.8.5 49.4-4.9-19.9-5.6-48.1-2.7-51.4.8.1 1.5.7 2.2 2zm-1.6 120.5c10.7 18.5 24.2 34.4 39.1 46.2-21.6 4.9-41.3 13-58.9 20.2-4.2 1.7-8.3 3.4-12.3 5 13.3-24.1 24.4-51.4 32.1-71.4zm155.6 65.5c.1.2.2.5-.4.9h-.2c-14.6.8-41.2-4.7-68.7-18.5 27.7-5.3 47.8-4.8 57.4-1.1 4.2 1.6 8.2 5 11.9 18.7z" />
                    <path d="M854.6 288.6L639.4 73.4c-6-6-14.1-9.4-22.6-9.4H192c-17.7 0-32 14.3-32 32v832c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V311.3c0-8.5-3.4-16.7-9.4-22.7zM790.2 326H602V137.8L790.2 326zm1.8 562H232V136h302v216a42 42 0 0042 42h216v494z" />
                  </svg>
                  {form.getFieldValue("fileUrl").split("/").pop()}
                </a>
              </div>
              <p style={{ marginTop: 8 }}>
                Upload a new file to replace the current one:
              </p>
            </div>
          )}

          {/* File upload component */}
          <Form.Item
            name="file"
            label={resolvedFileLabel}
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList;
            }}
          >
            <Upload.Dragger
              name="file"
              beforeUpload={() => false}
              maxCount={1}
              accept=".pdf,.csv,.xlsx,.xls,.png,.jpg,.jpeg"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Support for a single file upload. Accepted formats: PDF, CSV,
                Excel, Images.
              </p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
              >
                {resolvedSubmitLabel}
              </Button>
              {onSplitInvoice && invoiceItems.length > 0 && (
                <Button
                  type="default"
                  onClick={onSplitInvoice}
                  loading={loading}
                  style={{
                    borderColor: "#1890ff",
                    color: "#1890ff",
                    fontWeight: "bold",
                  }}
                >
                  {splitButtonText ||
                    (defaultInvoiceType === "bill"
                      ? "Split Bill"
                      : "Split Invoice")}
                </Button>
              )}
              <Button
                onClick={() => {
                  clearDraft();
                  navigate(cancelPath);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </>
  );
};

export default InvoiceFormLayout;
